import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
    Insights as InsightsIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { UserEngagement } from '../../../api/adminApi';
import { adminTokens as T } from '../../../theme/adminTokens';

type Tone = 'neutral' | 'good' | 'warning' | 'error';

const TONE_PALETTE: Record<Tone, { bg: string; text: string }> = {
    neutral: { bg: T.color.surface.sunken, text: T.color.text.primary },
    good:    { bg: T.color.success.soft,   text: T.color.success.text },
    warning: { bg: T.color.warning.soft,   text: T.color.warning.text },
    error:   { bg: T.color.error.soft,     text: T.color.error.text },
};

const Tile: React.FC<{ label: string; value: React.ReactNode; tone?: Tone; hint?: string }> = ({ label, value, tone = 'neutral', hint }) => {
    const p = TONE_PALETTE[tone];
    return (
        <Box sx={{ bgcolor: p.bg, borderRadius: `${T.radius.md}px`, p: `${T.space[3]}px`, textAlign: 'center' }}>
            <Typography sx={{ fontSize: T.type.size.xl, fontWeight: T.type.weight.bold, color: p.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', mt: `${T.space[1]}px` }}>
                <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>{label}</Typography>
                {hint && (
                    <Tooltip title={hint} arrow>
                        <InfoIcon sx={{ fontSize: 13, color: T.color.text.tertiary }} />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const fmtPct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}%`);
const fmtNum = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString());

/**
 * Moderation-focused engagement panel shared by the Report Details and
 * User Profile screens. Highlights low keep-rate and inbound blocks, the two
 * strongest signals for fake / abusive accounts.
 */
const EngagementPanel: React.FC<{ engagement?: UserEngagement; sx?: any }> = ({ engagement, sx }) => {
    if (!engagement) return null;

    const e = engagement;
    const decisionsReceived = e.keeps_received + e.pops_received;

    // Keep-rate tone: only meaningful with a reasonable sample size.
    let keepRateTone: Tone = 'neutral';
    if (e.keep_rate_received != null && decisionsReceived >= 5) {
        if (e.keep_rate_received < 0.15) keepRateTone = 'error';
        else if (e.keep_rate_received < 0.35) keepRateTone = 'warning';
        else if (e.keep_rate_received >= 0.6) keepRateTone = 'good';
    }
    const blocksTone: Tone = e.blocks_received >= 3 ? 'error' : e.blocks_received > 0 ? 'warning' : 'neutral';

    const hasDepth = e.avg_time_spent_seconds != null || e.avg_photos_viewed != null || e.bio_view_rate != null;

    return (
        <Box sx={{
            bgcolor: T.color.surface.raised,
            border: `1px solid ${T.color.surface.border}`,
            borderRadius: `${T.radius.lg}px`,
            boxShadow: T.shadow.sm,
            p: `${T.space[5]}px`,
            ...sx,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: `${T.space[2]}px`, mb: `${T.space[4]}px` }}>
                <InsightsIcon sx={{ color: T.color.text.tertiary, fontSize: 20 }} />
                <Typography sx={{ fontSize: T.type.size.lg, fontWeight: T.type.weight.bold, color: T.color.text.primary }}>
                    Engagement
                </Typography>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: `${T.space[2]}px`,
            }}>
                <Tile label="Matches" value={fmtNum(e.matches)} tone={e.matches > 0 ? 'good' : 'neutral'} />
                <Tile
                    label="Keep-rate"
                    value={fmtPct(e.keep_rate_received)}
                    tone={keepRateTone}
                    hint="Share of people who kept this user (keeps ÷ keeps + pops received). Low values can indicate a fake or low-quality profile."
                />
                <Tile label="Keeps received" value={fmtNum(e.keeps_received)} />
                <Tile label="Pops received" value={fmtNum(e.pops_received)} />
                <Tile label="Keeps made" value={fmtNum(e.keeps_made)} />
                <Tile label="Pops made" value={fmtNum(e.pops_made)} />
                <Tile
                    label="Blocks received"
                    value={fmtNum(e.blocks_received)}
                    tone={blocksTone}
                    hint="How many users have blocked this person. A strong harassment / safety signal."
                />
                <Tile label="Blocks made" value={fmtNum(e.blocks_made)} />
            </Box>

            {hasDepth && (
                <>
                    <Box sx={{ height: '1px', bgcolor: T.color.surface.border, my: `${T.space[4]}px` }} />
                    <Typography sx={{ fontSize: T.type.size.xs, fontWeight: T.type.weight.semibold, color: T.color.text.tertiary, textTransform: 'uppercase', letterSpacing: 0.5, mb: `${T.space[2]}px` }}>
                        Viewing behavior · last 30 days
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: `${T.space[4]}px` }}>
                        <Box>
                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                                {e.avg_time_spent_seconds != null ? `${Math.round(e.avg_time_spent_seconds)}s` : '—'}
                            </Typography>
                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>Avg. time / profile</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                                {e.avg_photos_viewed != null ? e.avg_photos_viewed.toFixed(1) : '—'}
                            </Typography>
                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>Avg. photos viewed</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                                {fmtPct(e.bio_view_rate)}
                            </Typography>
                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>Bio-view rate</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: T.type.size.sm, fontWeight: T.type.weight.semibold, color: T.color.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                                {fmtNum(e.profile_views)}
                            </Typography>
                            <Typography sx={{ fontSize: T.type.size.xs, color: T.color.text.secondary }}>Profiles viewed</Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default EngagementPanel;
